import { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Search, X, Shield, MapPin } from "lucide-react";
import {
  buildAdminStoreOptions,
  filterAdminStores,
  type AdminStoreOption,
} from "./AdminStorePicker.utils";

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

interface AdminStorePickerProps {
  retailer: string;
  stores: unknown[] | null | undefined;
  selectedStoreId: string;
  onSelect: (storeId: string) => void;
  t: TranslateFn;
  loading?: boolean;
  showHeader?: boolean;
  expanded?: boolean;
}

function AdminStoreRow({
  store,
  isSelected,
  onSelect,
  t,
}: {
  store: AdminStoreOption;
  isSelected: boolean;
  onSelect: (storeId: string) => void;
  t: TranslateFn;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(store.normalizedId)}
      className={`w-full px-3 py-2.5 text-left transition-colors border-b border-gray-100 dark:border-dark-700 ${
        isSelected
          ? "bg-amber-50 dark:bg-amber-900/30"
          : "hover:bg-gray-50 dark:hover:bg-dark-800"
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">
          #{store.normalizedId}
        </span>
        <span className="text-xs text-gray-700 dark:text-dark-200 line-clamp-2 leading-relaxed">
          {store.normalizedAddress || t("products.adminStoreUnknownAddress")}
        </span>
      </div>
    </button>
  );
}

export default function AdminStorePicker({
  retailer,
  stores,
  selectedStoreId,
  onSelect,
  t,
  loading = false,
  showHeader = true,
  expanded = false,
}: AdminStorePickerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const allOptions = useMemo(
    () => buildAdminStoreOptions(stores, retailer),
    [stores, retailer],
  );
  const filtered = useMemo(
    () => filterAdminStores(allOptions, searchQuery),
    [allOptions, searchQuery],
  );

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 52,
    overscan: 12,
  });

  const isCustomSelected =
    selectedStoreId !== "all" &&
    selectedStoreId !== "online" &&
    !allOptions.some((store) => store.normalizedId === selectedStoreId);

  const handleSelect = (storeId: string) => {
    onSelect(storeId);
    setSearchQuery("");
  };

  return (
    <div
      className={`rounded-xl border border-amber-200/80 dark:border-amber-700/50 bg-gradient-to-br from-amber-50/90 to-orange-50/40 dark:from-amber-950/30 dark:to-orange-950/20 ${
        expanded ? "flex flex-col flex-1 min-h-0" : "overflow-hidden"
      } ${showHeader ? "mb-5" : ""}`}
    >
      {showHeader && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-200/60 dark:border-amber-800/40 bg-amber-100/50 dark:bg-amber-900/20">
          <Shield size={14} className="text-amber-700 dark:text-amber-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">
            {t("products.adminStorePickerTitle")}
          </span>
        </div>
      )}

      <div className={`p-3 ${expanded ? "flex flex-col flex-1 min-h-0 gap-3" : "space-y-3"}`}>
        <div className="relative flex-shrink-0">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-500"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("products.adminStoreSearchPlaceholder")}
            className="w-full pl-9 pr-8 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-dark-200"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {isCustomSelected && (
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-amber-100/80 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800">
            <MapPin size={13} className="text-amber-700 dark:text-amber-400 flex-shrink-0" />
            <span className="text-xs font-medium text-amber-900 dark:text-amber-200">
              {t("products.adminStoreActive", { storeId: selectedStoreId })}
            </span>
          </div>
        )}

        {searchQuery && !loading && (
          <p className="text-[10px] text-gray-500 dark:text-dark-400 flex-shrink-0">
            {filtered.length === 0
              ? t("products.adminStoreNoResults")
              : t("products.adminStoreMatchCount", { count: filtered.length })}
          </p>
        )}

        <div
          ref={listRef}
          className={`overflow-y-auto rounded-lg border border-gray-100 dark:border-dark-700 bg-white/70 dark:bg-dark-900/40 ${
            expanded ? "flex-1 min-h-[280px]" : "max-h-52"
          }`}
        >
          {loading ? (
            <div className="py-8 flex justify-center">
              <div className="w-5 h-5 border-2 border-amber-200 border-t-amber-600 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-6 px-3 text-center text-xs text-gray-500 dark:text-dark-400">
              {searchQuery
                ? t("products.adminStoreNoResults")
                : t("products.adminStoreEmpty")}
            </p>
          ) : (
            <div
              style={{ height: `${virtualizer.getTotalSize()}px`, width: "100%", position: "relative" }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const store = filtered[virtualItem.index];
                if (!store) return null;
                return (
                  <div
                    key={store.normalizedId}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <AdminStoreRow
                      store={store}
                      isSelected={selectedStoreId === store.normalizedId}
                      onSelect={handleSelect}
                      t={t}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {!loading && allOptions.length > 0 && !searchQuery && (
          <p className="text-[10px] text-gray-400 dark:text-dark-500 text-center flex-shrink-0">
            {t("products.adminStoreCatalogCount", { count: allOptions.length })}
          </p>
        )}
      </div>
    </div>
  );
}
